/**
 * Light/dark toggle for the KG-internal team app. Mirrors the
 * client-portal hook (lib/portal-theme.ts) so both surfaces share the
 * same pattern but persist their own preference independently. The
 * team user might prefer dark while the client share-token is light.
 *
 * Adds the `.dark` class to <html> when active so the global CSS
 * rules (in styles.css) repaint body bg + colour scheme. Cleans up on
 * unmount.
 */
import { useCallback, useEffect, useState } from 'react';

export type AppTheme = 'light' | 'dark';

const STORAGE_KEY = 'kg.app.theme';

function readInitial(): AppTheme {
  // Default to dark; users who've explicitly chosen light keep their
  // pick (stored value of 'light' wins). New users / fresh browsers
  // land on dark.
  if (typeof window === 'undefined') return 'dark';
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === 'light') return 'light';
    if (v === 'dark') return 'dark';
    return 'dark';
  } catch {
    return 'dark';
  }
}

export function useAppTheme(): {
  theme: AppTheme;
  toggle: () => void;
  setTheme: (t: AppTheme) => void;
} {
  const [theme, setThemeState] = useState<AppTheme>(readInitial);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Storage unavailable (private mode etc); in-memory state is
      // authoritative for this tab.
    }
  }, [theme]);

  useEffect(() => {
    if (theme !== 'dark') return;
    const root = document.documentElement;
    root.classList.add('dark');
    return () => {
      root.classList.remove('dark');
    };
  }, [theme]);

  const setTheme = useCallback((t: AppTheme) => setThemeState(t), []);
  const toggle = useCallback(
    () => setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark')),
    [],
  );

  return { theme, toggle, setTheme };
}
